import { useState, useEffect } from 'react';
import { BtnPrimary, BtnDefault } from '../common/Buttons';
import { JobListing } from '../../types/jobs';
import { useParams, useNavigate } from 'react-router-dom';
import StaticApplicantFields from './StaticApplicantFields';
import { LoaderCircle, Trash2 } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';

interface ApplicantFieldsFormProps {
  onSubmit: (fields: any[]) => void;
  isSubmitting: boolean;
  jobListings: JobListing[];
}

const FIELD_TYPES = [
  'text',
  'number',
  'textarea',
  'select',
  'multiSelect',
  'date',
];

const TOOLS = [
  'Gorgias',
  'Zendesk',
  'Freshdesk',
  'Salesforce',
  'Zoho Desk',
  'LiveChat',
];

const CHANNELS = ['Email', 'Voice', 'Chat', 'Social Media Suite'];

const ApplicantFieldsForm: React.FC<ApplicantFieldsFormProps> = ({
  onSubmit,
  isSubmitting,
  jobListings,
}) => {
  const { category } = useParams();
  const navigate = useNavigate();
  // Get job listings that match the current category
  const matchingJobs = category
    ? jobListings.filter(
        (job) => job.category?.toLowerCase() === category.toLowerCase()
      )
    : [];

  // Get metadata fields from matching jobs
  const categoryFields =
    matchingJobs.length > 0 && matchingJobs[0].metadata
      ? matchingJobs[0].metadata.map((field) => ({ ...field, selected: true }))
      : [];

  const [selectedFields, setSelectedFields] = useState<any[]>(categoryFields);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [suggestedFields, setSuggestedFields] = useState({
    tools: false,
    channels: false,
  });

  // Update selectedFields when category or jobListings change
  useEffect(() => {
    if (matchingJobs.length > 0 && matchingJobs[0].metadata) {
      setSelectedFields(
        matchingJobs[0].metadata.map((field) => ({ ...field, selected: true }))
      );
    } else {
      setSelectedFields([]);
    }
  }, [category, jobListings]);

  const toggleSelect = (index: number) => {
    const newFields = [...selectedFields];
    newFields[index].selected = !newFields[index].selected;
    setSelectedFields(newFields);
  };

  const addCustomField = () => {
    setCustomFields([
      ...customFields,
      { label: '', type: 'text', required: false, options: [] },
    ]);
  };

  const handleCustomChange = (i: number, key: string, value: any) => {
    const updated = [...customFields];
    updated[i][key] = value;
    setCustomFields(updated);
  };

  const addOption = (fieldIndex: number) => {
    const updated = [...customFields];
    if (!updated[fieldIndex].options) {
      updated[fieldIndex].options = [];
    }
    updated[fieldIndex].options.push('');
    setCustomFields(updated);
  };

  const updateOption = (
    fieldIndex: number,
    optionIndex: number,
    value: string
  ) => {
    const updated = [...customFields];
    updated[fieldIndex].options[optionIndex] = value;
    setCustomFields(updated);
  };

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const updated = [...customFields];
    updated[fieldIndex].options.splice(optionIndex, 1);
    setCustomFields(updated);
  };

  const removeCustomField = (index: number) => {
    const updated = [...customFields];
    updated.splice(index, 1);
    setCustomFields(updated);
  };

  const toggleSuggestedField = (fieldName: 'tools' | 'channels') => {
    setSuggestedFields((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handleSaveAndFinish = () => {
    // Combine the selected predefined fields and custom fields
    const predefinedSelectedFields = selectedFields
      .filter((f) => f.selected)
      .map(({ selected, ...rest }) => rest);

    // Add suggested fields if selected
    const selectedSuggestedFields = [];
    if (suggestedFields.tools) {
      selectedSuggestedFields.push({
        label: 'Tools',
        type: 'multiSelect',
        required: false,
        options: TOOLS,
      });
    }
    if (suggestedFields.channels) {
      selectedSuggestedFields.push({
        label: 'Channels',
        type: 'multiSelect',
        required: false,
        options: CHANNELS,
      });
    }

    const finalFields = [
      ...predefinedSelectedFields,
      ...selectedSuggestedFields,
      ...customFields,
    ];
    onSubmit(finalFields);
  };

  return (
    <div>
      <BtnDefault
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 my-4"
      >
        <ArrowLeft size={14} />
        Back
      </BtnDefault>
      <div className="bg-white p-6 rounded-lg shadow-sm border space-y-6">
        <h1 className="text-xl font-bold mb-4">Applicant Form</h1>

        {/* Static fields section */}
        <StaticApplicantFields />

        <div className="border-t pt-4">
          <h2 className="text-lg font-semibold mb-3">Suggested Fields</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-1 border border-greyFourty rounded-lg py-2 px-3 max-h-[65px]">
              <input
                type="checkbox"
                className="mr-2"
                checked={suggestedFields.tools}
                onChange={() => toggleSuggestedField('tools')}
              />
              <div>
                <h3 className="text-md font-medium">Tools</h3>
                <p className="text-xs text-greyNinety">Type: multiselect</p>
              </div>
            </div>
            <div className="flex items-center gap-1 border border-greyFourty rounded-lg py-2 px-3 max-h-[65px]">
              <input
                type="checkbox"
                className="mr-2"
                checked={suggestedFields.channels}
                onChange={() => toggleSuggestedField('channels')}
              />
              <div>
                <h3 className="text-md font-medium">Channels</h3>
                <p className="text-xs text-greyNinety">Type: multiselect</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic fields section (from metadata) */}
        {selectedFields.length > 0 && (
          <div className="mt-4">
            <h2 className="text-lg font-semibold mb-3">Custom Fields</h2>
            <div className="pb-4">
              {selectedFields.map((field, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-4 p-2 hover:bg-greyTen border rounded-lg ${idx === selectedFields.length - 1 ? 'mb-0' : 'mb-2'}`}
                >
                  <input
                    type="checkbox"
                    checked={field.selected}
                    onChange={() => toggleSelect(idx)}
                    className="h-4 w-4"
                  />
                  <div className="flex-1">
                    <span className="font-medium">{field.label}</span>
                    <div className="text-xs text-greyNinety mt-1">
                      Type: {field.type} •{' '}
                      {field.required ? 'Required' : 'Optional'}
                      {field.options && field.options.length > 0 && (
                        <span> • Options: {field.options.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom fields */}
        <div>
          <div className="space-y-3">
            {customFields.map((field, i) => (
              <div
                key={i}
                className="border p-4 rounded-lg bg-white shadow-sm mb-4"
              >
                <div className="flex justify-between mb-3">
                  <h4 className="font-medium">
                    {field.label || 'Unnamed Field'}
                  </h4>
                  <button
                    onClick={() => removeCustomField(i)}
                    className="text-red-500 hover:text-red-700 cursor-pointer hover:bg-greyTwenty rounded-md p-1.5"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-sm mb-1">Field Label</label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) =>
                        handleCustomChange(i, 'label', e.target.value)
                      }
                      placeholder="Enter field label"
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-1">Field Type</label>
                    <select
                      value={field.type}
                      onChange={(e) =>
                        handleCustomChange(i, 'type', e.target.value)
                      }
                      className="w-full p-2 border rounded"
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) =>
                        handleCustomChange(i, 'required', e.target.checked)
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-sm">Required field</span>
                  </label>
                </div>

                {(field.type === 'select' || field.type === 'multiSelect') && (
                  <div className="pl-3 border-l-2 border-blue-200 mt-3">
                    <h4 className="text-sm font-medium mb-2">Options</h4>
                    {field.options && field.options.length > 0 ? (
                      field.options.map((option: string, optionIdx: number) => (
                        <div key={optionIdx} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={option}
                            onChange={(e) =>
                              updateOption(i, optionIdx, e.target.value)
                            }
                            placeholder="Option value"
                            className="flex-grow p-1 border rounded text-sm"
                          />
                          <button
                            onClick={() => removeOption(i, optionIdx)}
                            className="px-2 text-red-500"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 mb-2">
                        No options added
                      </p>
                    )}
                    <button
                      onClick={() => addOption(i)}
                      className="text-sm text-blue-500 flex items-center"
                    >
                      + Add Option
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <BtnDefault onClick={addCustomField} className="my-4 py-2 w-fit">
            + Add New Field
          </BtnDefault>
        </div>

        <div className="mt-6 border-t pt-4">
          <div className="flex justify-end">
            <BtnPrimary
              onClick={handleSaveAndFinish}
              disabled={isSubmitting}
              className="w-20 py-2"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <span className="ml-2">Saving</span>
                  <LoaderCircle size={16} className="animate-spin" />
                </div>
              ) : (
                'Save'
              )}
            </BtnPrimary>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicantFieldsForm;
